import { trackEvent } from 'bits/analytics'
import { captureMessage } from 'bits/error_tracker'
import { getName, getEmbeddedUrl } from 'bits/file_handler'
import ajax from 'bits/ajax'
import EventEmitter from 'events'
import defer from 'lodash.defer'
let uploadsInProgress = []
const FILE_TOO_LARGE_ERROR = 'FILE_TOO_LARGE_ERROR'

const trackError = function(label, context = {}) {
  trackEvent('Uploads', 'Error', label)
  context['fileType'] = label
  captureMessage('UploadError', { context })
}

const trackSuccess = function(label) {
  trackEvent('Uploads', 'Success', label)
}

class UploadJob extends EventEmitter {
  constructor(file, options = {}) {
    super()
    this.file = file
    this.options = options
  }

  cancel() {
    if (this.xhr1) this.xhr1.abort()
    if (this.xhr2) this.xhr2.abort()
    this.emit('cancel')
  }

  perform() {
    let maxFileSizeBytes = this.options.maxFileSize
    // We give 1MB extra. Psychologically, 25.5MB is 25MB, not 26MB.
    if (maxFileSizeBytes) maxFileSizeBytes = maxFileSizeBytes + 1048576
    if (maxFileSizeBytes && this.file.size > maxFileSizeBytes) {
      trackEvent('Uploads', 'Large file', this.file.type, this.file.size)
      this._reject({ error: FILE_TOO_LARGE_ERROR })
    } else {
      // Sometimes, a file can just be a holder of a url. (e.g. gdoc file)
      // It so, try to get the URL and just return that.
      getEmbeddedUrl(this.file).then(this._resolve.bind(this), this._ajax1.bind(this))
    }
  }

  _progress(e, timeStart) {
    const loadRatio = e.loaded / e.total
    // const timeNow = new Date()
    // const timeElapsed = timeNow - timeStart
    // console.log(timeElapsed)
    // const expectedTimeToProcess = 3000 //3 seconds
    // const loadRatio = e.loaded / e.total
    // const expectedTimeToUpload = timeElapsed / loadRatio
    // const expectedTimeToUploadAndProcess = expectedTimeToUpload + expectedTimeToProcess;
    // const actualProgress = timeElapsed/expectedTimeToUploadAndProcess
    this.emit('progress', loadRatio)
  }

  _resolve(obj) {
    this.emit('done', obj)
  }

  _reject(obj) {
    this.emit('error', obj)
  }

  _ajax1() {
    const self = this
    this.xhr1 = ajax({
      url: '/media/upload_attributes.json',
      dataType: 'json',
      type: 'OPTIONS',
      cache: false,
      data: {
        unique: +new Date(),
      },
      error(responseData, textStatus) {
        if (textStatus == 'abort') return
        trackError('ajax1', {
          statusCode: responseData.status,
          readyState: responseData.readyState,
        })
        self._reject(responseData)
      },
      success(attributes) {
        self._ajax2(attributes)
      },
    })
  }

  _ajax2(attributes) {
    const timeStart = new Date()
    const self = this
    const formData = new FormData()
    const name = getName(this.file)
    const originalFilename = this.file.name ? this.file.name : name

    const fieldsOverrides = {}
    if (this.options.maxFileSize) {
      fieldsOverrides.max_file_size = this.options.maxFileSize + 1048576
      fieldsOverrides.transcode = true
    }
    const fields = Object.assign({}, attributes.fields, fieldsOverrides)
    Object.entries(fields).forEach(x => {
      if (x[0] != 'key') formData.append(x[0], x[1])
    })

    formData.append('key', [attributes.fields.key, name].join('/'))
    formData.append('Content-Type', this.file.type)
    formData.append('file_size', this.file.size)
    formData.append('title', originalFilename)
    formData.append('file', this.file, name)

    this.xhr2 = ajax({
      url: attributes.url,
      crossDomain: true,
      type: 'POST',
      data: formData,
      cache: false,
      contentType: false,
      processData: false,
      dataType: 'json',
      xhr() {
        const x = $.ajaxSettings.xhr()
        if (x.upload) {
          x.upload.addEventListener('progress', e => {
            if (e.lengthComputable) {
              self._progress(e, timeStart)
            }
          })
        }
        return x
      },
      error(responseData, textStatus) {
        if (textStatus == 'abort') return
        trackError(self.file.type, {
          statusCode: responseData.status,
          userId: fields.uploader_id,
          filename: self.file.name,
          filsSize: self.file.size,
          maxFileSize: fields.max_file_size || 'None',
          readyState: responseData.readyState,
          responseText: responseData.responseText,
          responseXml: responseData.responseXml, //could be returned by nginx/lb
        })
        self._reject(responseData)
      },
      success(responseData) {
        trackSuccess(self.file.type)
        self._resolve(responseData)
      },
    })
  }
}

const startUpload = function(file, options = {}) {
  const uploadJob = new UploadJob(file, options)
  uploadJob.on('done', result => {
    uploadsInProgress = uploadsInProgress.filter(x => x != uploadJob)
  })
  uploadJob.on('error', error => {
    uploadsInProgress = uploadsInProgress.filter(x => x != uploadJob)
  })
  uploadJob.on('cancel', error => {
    uploadsInProgress = uploadsInProgress.filter(x => x != uploadJob)
  })
  uploadsInProgress.push(uploadJob)
  defer(() => uploadJob.perform())
  return uploadJob
}

const stopUpload = function(file) {
  const uploadJob = uploadsInProgress.find(x => x.file == file)
  if (uploadJob) {
    uploadJob.cancel()
  }
}

export { startUpload, stopUpload, UploadJob, FILE_TOO_LARGE_ERROR }
